/**
 * @file 画面をまたいで使い回す一覧の覚えである (資料の一覧・学びメモ)。
 *
 * @what: key ごとに値と取得した時刻を持ち、取得中の Promise も共有する。期限内は取り直さず、
 *   期限を過ぎたら覚えを返しつつ取り直す (どちらにするかは hook が readShared の fresh で決める)。
 *   書き込みのあとは invalidateShared で捨てる。
 * @why: 資料の一覧 (目次込みで約360KB) と学びメモの全件は、本だな・リーダー・さくいん・解説の面が
 *   mount のたびに取り直していた。値は家族の持ち物で画面をまたいでも同じなので、モジュールの覚えで
 *   1回にする。
 * @failure: 取得の失敗は覚えに残さない (次の mount で取り直す)。invalidate のあとに届いた取得の結果は
 *   覚えない (古い値を書き戻さない)。サインアウトで空にする (lib/studyImageCacheReset.ts)。テストは
 *   afterEach で resetSharedFetchCache を呼ぶ (tests/setup.ts)。
 */

type Entry = { value: unknown; at: number };

const values = new Map<string, Entry>();
const inflight = new Map<string, Promise<unknown>>();
/** invalidate のたびに進める番号で、進行中の取得が古い結果を書き戻さないために見る。 */
const generation = new Map<string, number>();

/** 覚えを読む。無ければ null。fresh は取得から ttlMs 以内かどうかである。 */
export function readShared<T>(
  key: string,
  ttlMs: number,
  now: number = Date.now(),
): { value: T; fresh: boolean } | null {
  const hit = values.get(key);
  if (!hit) return null;
  return { value: hit.value as T, fresh: now - hit.at < ttlMs };
}

/** 取得して覚える。同じ key の取得が進行中なら、その Promise を返す (取得は1回になる)。 */
export function loadShared<T>(
  key: string,
  fetcher: () => Promise<T>,
  now: () => number = Date.now,
): Promise<T> {
  const running = inflight.get(key);
  if (running) return running as Promise<T>;
  const gen = generation.get(key) ?? 0;
  const promise: Promise<T> = fetcher().then(
    (value) => {
      if ((generation.get(key) ?? 0) === gen) values.set(key, { value, at: now() });
      if (inflight.get(key) === promise) inflight.delete(key);
      return value;
    },
    (err: unknown) => {
      if (inflight.get(key) === promise) inflight.delete(key);
      throw err;
    },
  );
  inflight.set(key, promise);
  return promise;
}

/** 書き込みのあとに呼び、その key の覚えを捨てる。進行中の取得の結果も覚えない。 */
export function invalidateShared(key: string): void {
  values.delete(key);
  inflight.delete(key);
  generation.set(key, (generation.get(key) ?? 0) + 1);
}

/** 全部捨てる (サインアウトとテスト用)。 */
export function resetSharedFetchCache(): void {
  values.clear();
  inflight.clear();
  generation.clear();
}
